import { TrackingUserGroup, TrackingUser, TrackingUserDevice, TrackingUserPosition, TrackingUserTask, TrackingUserTasksFilters, TrackingUserTaskLocationStatus, TrackingUserSession, PageRequestTrackingFilters, PageRequestTrackingUserSession, LocationStatusFills } from '@/types/Tracking'
import moment from 'moment'
import api from '@/api'
import config from '@/config'
import cookiesService from '@/services/support/CookiesService'
import authService from '@/services/support/AuthService'
import { CursorRequest, CursorResponse, PageRequest, PageResponse } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { Role } from '@/types/Catalogs'
import CatalogsService from './CatalogsService'
import { GroupCatalogFilters, GroupStatus } from '@/types/catalogs/Groups'

declare const Buffer: any
const DATE_FILTER_FORMAT = 'YYYY-MM-DDTHH:mm:ss'

class TrackingService {
  // Request to Tracker API
  /**
   * @deprecated
   */
  async getUserGroups (): Promise<TrackingUserGroup[]> {
    const domainId = await authService.getDomainId()
    const response = await api.get(`/tracker/domains/${domainId}/groups`)
    const groups = response.data.groups
    return groups
  }

  /**
   * Consults groups to Users API
   *
   * @returns a TrackingUsersGroup list (groups info)
   * @deprecated
   */
  async getGroupsAPIUSERS (): Promise<TrackingUserGroup[]> {
    const domainId = await authService.getDomainId()
    const request = new PageRequest<GroupCatalogFilters>()
    request.sort = 'name'
    if (userInfoStore.inRootDomain()) {
      request.filters.domain = domainId
    }
    request.filters.status = GroupStatus.ACTIVE
    const response = await CatalogsService.getGroups(request)
    const groups = response.results
    return groups
  }

  /**
   * @deprecated
   */
  async getPagedUsers (pageRequest: PageRequest<any>): Promise<PageResponse<TrackingUser>> {
    const projectId = cookiesService.get(config.projectIdCookieName)
    const response = await api.get(`tracker/projects/${projectId}/templates/users?offset=${pageRequest.page * pageRequest.size}&limit=${pageRequest.size}`)

    const pageResponse = new PageResponse<TrackingUser>()
    pageResponse.content = response.data.results
    pageResponse.totalElements = response.data.total

    pageResponse.content.forEach((it: TrackingUser) => {
      it.status = 0
      it.device = it.device || new TrackingUserDevice()
      it.position = new TrackingUserPosition()
    })

    return pageResponse
  }

  // Request to Tracker API
  /**
   * @deprecated
   */
  async getPagedUsersByGroup (groupId: number, pageRequest: PageRequest<PageRequestTrackingFilters>): Promise<PageResponse<TrackingUser>> {
    const domainId = await authService.getDomainId()
    let requestUrl = `tracker/domains/${domainId}/groups/${groupId}/users?offset=${pageRequest.page * pageRequest.size}&limit=${pageRequest.size}`

    // Filters
    requestUrl += pageRequest.filters.onlyActiveUsers !== undefined ? `&active=${pageRequest.filters.onlyActiveUsers}` : ''
    requestUrl += pageRequest.filters.onlyMobileUsers !== undefined ? `&mobile-only=${pageRequest.filters.onlyMobileUsers}` : ''
    requestUrl += pageRequest.filters.from ? `&from=${pageRequest.filters.from}` : ''
    requestUrl += pageRequest.filters.to ? `&to=${pageRequest.filters.to}` : ''

    const response = await api.get(requestUrl)

    const pageResponse = new PageResponse<TrackingUser>()
    pageResponse.content = response.data.results
    pageResponse.totalElements = response.data.total

    pageResponse.content.forEach((it: TrackingUser) => {
      it.status = 0
      it.device = it.device || new TrackingUserDevice()
      it.position = new TrackingUserPosition()
    })

    return pageResponse
  }

  /**
   * Returns a page of users info, filter by a specific group and parameters from request.
   *
   * @param groupId id from group to get users
   * @param roles from the users to consider
   * @param pageRequest with parameters to create API request
   * @returns a page TrackingUser PageRequest (users info)
   */
  async getPagedUsersByGroupAPIUSERS (groupId: number, roles: Role[], pageRequest: PageRequest<PageRequestTrackingFilters>) {
    // console.log('>> USUARIOS desde API USERS ')
    const domainId = await authService.getDomainId()
    let usersRequest = `${config.apiUsersUrl}users?offset=${pageRequest.page * pageRequest.size}&limit=${pageRequest.size}`
    usersRequest += `&domain=${domainId}`
    usersRequest += `&group=${groupId}`
    usersRequest += `&sub-groups=true`
    usersRequest += `&load-relations=true` // is active because the groups related are needed
    usersRequest += `&copy-from-role=true`

    // Add 'roles' and 'copy-from-role' params
    for (const role of roles) {
      usersRequest += `&role=${role.id}`
    }

    // Prepare 'from' and 'to' dates as params
    const maxPeriodRange = 31
    const toISO = pageRequest.filters.to ? moment(pageRequest.filters.to) : moment()
    const fromISO = pageRequest.filters.from ? moment(pageRequest.filters.from) : toISO.clone().subtract(maxPeriodRange, 'days')
    usersRequest += `&from=${fromISO.format('YYYY-MM-DD')}`
    usersRequest += `&to=${toISO.format('YYYY-MM-DD')}`

    // Add license-type filter
    if (pageRequest.filters.onlyActiveUsers !== undefined) {
      usersRequest += pageRequest.filters.onlyActiveUsers ? '&license-type=active' : 'license-type=inactivated'
    } else {
      usersRequest += '&license-type=active'
    }

    // Create page response
    const pageResponse = new PageResponse<TrackingUser>()
    await api.get(usersRequest).then(response => {
      pageResponse.content = response.data.results
      pageResponse.totalElements = response.data.total
    })

    pageResponse.content.forEach((it: TrackingUser) => {
      it.status = 0
      it.device = it.device || new TrackingUserDevice()
      it.position = new TrackingUserPosition()
    })
    return pageResponse
  }

  /**
   * Gets the tasks from a determinated user
   *
   * @param pageRequest - configurations to tasks
   * @returns a task list
   * @deprecated
   */
  async getPagedUserTasks (pageRequest: PageRequest<TrackingUserTasksFilters>): Promise<PageResponse<TrackingUserTask>> {
    const filters = pageRequest.filters
    const from = moment(filters.initDate).set({ hour: 0, minute: 0, second: 0 })
    const to = moment(filters.endDate).set({ hour: 23, minute: 59, second: 59 })
    const response = await api.get(`tracker/templates/${filters.templateId}/tasks/${filters.userId}?from=${from.toDate().getTime()}&to=${to.toDate().getTime()}&offset=${pageRequest.page * pageRequest.size}&limit=${pageRequest.size}&worked=${filters.worked}`)

    const pageResponse = new PageResponse<TrackingUserTask>()
    pageResponse.content = response.data.results
    pageResponse.totalElements = response.data.total

    pageResponse.content.forEach(it => {
      it.status = this.getTaskStatus(it)
      it.fullIdent = (it.ident0 ? `${it.ident0.label} : ${it.ident0.value}` : '') + (it.ident1 ? ` - ${it.ident1.label} : ${it.ident1.value}` : '') + (it.ident2 ? ` - ${it.ident2.label} : ${it.ident2.value}` : '')
    })

    return pageResponse
  }

  /**
   * Gets the fills that are like tasks from a specific template
   * belongs to determinated  user by API Fills.
   * For worked tasks use query params:
   *   - limit
   *   - count
   *   - user
   *   - worked
   *   - from
   *   - to
   *   - location-status: in-site, out-of-site
   * For not worked tasks use query params:
   *   - limit
   *   - count
   *   - altUser
   *   - from
   *   - to
   *   - location-status: not-worked
   *
   * @param cursorRequest with config to get the fills
   * @param templateIdents a list of objects with the structure
   *  [{ name: <name>, label: <label> }...]
   * @returns fills like tasks list
   */
  async getPagedUserTasksAPIFILLS (
    cursorRequest: CursorRequest<TrackingUserTasksFilters>,
    templateIdents: any[]
  ) : Promise<CursorResponse<TrackingUserTask>> {
    const filters = cursorRequest.filters
    const url = `${config.apiFillsUrl}templates/${filters.templateId}/fills`
    const params = new URLSearchParams()
    params.append('limit', cursorRequest.limit.toString())
    if (cursorRequest.count) {
      params.append('count', cursorRequest.count.toString())
    }
    if (cursorRequest.cursor) {
      params.append('cursor', cursorRequest.cursor)
    }

    if (filters.worked) {
      params.append('user', filters.user.name)
      params.append('worked', filters.worked.toString())
    } else {
      params.append('altUser', filters.altUser.name)
    }

    for (const locationStatus of filters.locationStatus) {
      params.append('location-status', locationStatus)
    }

    const fromDate = new Date(filters.initDate)
    fromDate.setHours(0, 0, 0, 0)
    let toDate = new Date(filters.endDate)
    // If is the current day, we must consider until the current hour,
    // else consider all day
    const today = new Date()
    if (toDate.getFullYear() === today.getFullYear() &&
    toDate.getMonth() === today.getMonth() &&
    toDate.getDate() === today.getDate()
    ) {
      toDate = today
      toDate.setSeconds(0, 0)
    } else {
      toDate.setHours(23, 59, 59, 0)
    }
    // Gets a UTC format as the serve setting time
    const from = fromDate.toISOString()
    const to = toDate.toISOString()

    params.append('from', from)
    params.append('to', to)
    params.append('date-type', 'receivedDate')

    const response = await api.get(url, { params })

    const ident0Label = templateIdents.length > 0 ? templateIdents[0].label : 'Ident0 sin etiqueta'
    const ident1Label = templateIdents.length > 1 ? templateIdents[1].label : 'Ident1 sin etiqueta'
    const ident2Label = templateIdents.length > 2 ? templateIdents[2].label : 'Ident2 sin etiqueta'

    response.data.results.forEach((it: TrackingUserTask) => {
      it.status = this.getTaskStatus(it)
      it.fullIdent = ''
      it.fullIdent += ident0Label ? `${ident0Label}: ${it.ident0}` : ''
      it.fullIdent += ident1Label ? ` - ${ident1Label}: ${it.ident1}` : ''
      it.fullIdent += ident2Label ? ` - ${ident2Label}: ${it.ident2}` : ''
    })

    return response.data
  }

  getTaskStatus (task: TrackingUserTask): TrackingUserTaskLocationStatus {
    if (task.locationStatus === LocationStatusFills.IN_SITE) {
      return TrackingUserTaskLocationStatus.COMPLETE
    }
    if (task.locationStatus === LocationStatusFills.OUT_OF_SITE) {
      return TrackingUserTaskLocationStatus.COMPLETE_WARN
    }

    return TrackingUserTaskLocationStatus.UNCOMPLETE
  }

  getUserFullName (user: TrackingUser) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim()
  }

  getUserGroupsNames (user: TrackingUser) {
    if (user.groups) {
      const groupNames = user.groups.reduce<string[]>((arr, it) => arr.concat(it.name), [])
      return groupNames.join(', ')
    }

    return ''
  }

  async getUserSessions (pageRequest: PageRequest<PageRequestTrackingUserSession>): Promise<PageResponse<TrackingUserSession>> {
    const response = await api.get(`${config.apiUsersUrl}licenses?user=${pageRequest.filters.user}&limit=${pageRequest.size}&sort=${pageRequest.sort}&sort-order=${pageRequest.sortOrder}&offset=${pageRequest.offset}`)
    response.data.results = response.data.results.map((session: TrackingUserSession) => {
      const active = session.active
      const acquiredDate = moment.utc(session.acquiredDate).valueOf()
      const releaseDate = session.releaseDate ? moment.utc(session.releaseDate).valueOf() : null
      return releaseDate
        ? { active, acquiredDate, releaseDate }
        : { active, acquiredDate }
    })
    return response.data
  }

  filterValidTasks (tasksList: TrackingUserTask[]) {
    return tasksList.filter((it: TrackingUserTask) => {
      return (it.status === TrackingUserTaskLocationStatus.UNCOMPLETE &&
        it.fillLocation) ||
        (it.status !== TrackingUserTaskLocationStatus.UNCOMPLETE &&
          it.visitedLocation)
    })
  }
}

export default new TrackingService()
